import Header from "components/common/Header";
import DataList from "components/elements/DataList";
import LargeModal from "components/elements/LargeModal";
import Table from "components/elements/Table";
import {
  CLIENT_ADVERTISER,
  CLIENT_AGENCY,
  CLIENT_BROADCASTER,
  CLIENT_OTHERS,
  CONTENT_TYPE_REVERSE,
  MASTER_ADVERTISER,
  MASTER_BRAND,
  MASTER_CHANNEL,
  USER_BUSINESS_ADMIN,
  USER_CLIENT_ADMIN,
  USER_SUPERADMIN,
} from "core/Constants";
import dataProvider from "core/DataProvider";
import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import toast from "react-hot-toast";
import { useOutletContext } from "react-router-dom";

const consumptionHeaders = {
  id: "Group ID",
  name: "Group Name",
  total_spots: "Total Spots",
  consumed_spots: "Consumed Spots",
  consumption_percent: "Consumption Percentage",
};

const AdminDashboard = () => {
  const [cardData, setCardData] = useState({
    total_users: 0,
    total_subscriptions: 0,
    total_spots: 0,
    spots_consumed: 0,
    spots_available: 0,
    broadcaster_subscribed_channels: 0,
  });
  const [playoutCountData, setPlayoutCountData] = useState({
    commercial_count: 0,
    promo_count: 0,
    program_count: 0,
  });
  const [masterCountData, setMasterCountData] = useState({
    advertiser_count: 0,
    brand_count: 0,
    channel_count: 0,
  });
  const [criticalConsumptionGroups, setCriticalConsumptionGroups] = useState(
    []
  );
  const [playoutDates, setPlayoutDates] = useState(null);
  const [clientTypeCounts, setClientTypeCount] = useState([24, 89, 12, 1]);
  const [spotWiseData, setSpotWiseData] = useState([
    { name: "Commercial", data: [] },
    { name: "Promo", data: [] },
  ]);
  const [spotCountDates, setSpotCountDates] = useState([]);
  const [largeModalContent, setLargeModalContent] = useState(null);
  const [spotTrendLoader, setSpotTrendLoader] = useState(false);

  const { token } = useOutletContext();
  const { name, client_name } = token;

  const listData = [
    {
      title: "Commercials",
      desc: "Total number of commercial spots in the data date playouts",
      value: playoutCountData.commercial_count.toLocaleString("en-IN"),
      action: false,
      permissions: [USER_SUPERADMIN, USER_BUSINESS_ADMIN, USER_CLIENT_ADMIN],
    },
    {
      title: "Promos",
      desc: "Total number of promo spots in the data date playouts",
      value: playoutCountData.promo_count.toLocaleString("en-IN"),
      action: false,
      permissions: [USER_SUPERADMIN, USER_BUSINESS_ADMIN, USER_CLIENT_ADMIN],
    },
    {
      title: "Programs",
      desc: "Total number of unique programs in playouts",
      value: playoutCountData.program_count.toLocaleString("en-IN"),
      action: false,
      permissions: [USER_SUPERADMIN, USER_BUSINESS_ADMIN],
    },
    {
      title: "Advertiser",
      desc: "Total number of unique advertisers",
      value: masterCountData.advertiser_count.toLocaleString("en-IN"),
      action: false,
      permissions: [USER_SUPERADMIN, USER_BUSINESS_ADMIN],
    },
    {
      title: "Brands",
      desc: "Total number of unique brands",
      value: masterCountData.brand_count.toLocaleString("en-IN"),
      action: false,
      permissions: [USER_SUPERADMIN, USER_BUSINESS_ADMIN, USER_CLIENT_ADMIN],
    },
    {
      title: "Channels",
      desc: "Total number of unique channels",
      value: masterCountData.channel_count.toLocaleString("en-IN"),
      action: false,
      permissions: [USER_SUPERADMIN, USER_BUSINESS_ADMIN],
    },
    {
      title: "Critical Consumptions",
      desc: "Clients with spot consumption more than 80%",
      value: criticalConsumptionGroups.length,
      action: () => {
        setLargeModalContent(
          <div className="consumption-table">
            <Table
              headers={consumptionHeaders}
              data={criticalConsumptionGroups}
              pagination={true}
            />
          </div>
        );
      },
      permissions: [USER_SUPERADMIN, USER_BUSINESS_ADMIN],
    },
  ];

  useEffect(() => {
    fetchDashCardNumbers();
    fetchPlayoutTypeCounts();
    fetchMasterCounts();
    fetchPlayoutDates();
    fetchClientTypeCounts();
    fetchCriticalConsumptionGroups();

    setTimeout(() => {
      fetchSpotTypeCounts();
    }, 500);
  }, [token.client_id]);

  const fetchDashCardNumbers = async () => {
    try {
      const data = await dataProvider.network.get("dash/card/numbers");
      setCardData(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load card numbers");
    }
  };

  const fetchPlayoutTypeCounts = async () => {
    try {
      const data = await dataProvider.network.get("dash/playout/type-counts");
      const ns = { ...playoutCountData };
      for (let item of data) {
        if (item.id === CONTENT_TYPE_REVERSE.Commercial) {
          ns.commercial_count = item.value;
        }
        if (item.id === CONTENT_TYPE_REVERSE.Promo) {
          ns.promo_count = item.value;
        }
        if (item.id === CONTENT_TYPE_REVERSE.Program) {
          ns.program_count = item.value;
        }
      }

      setPlayoutCountData(ns);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load playout type counts");
    }
  };

  const fetchMasterCounts = async () => {
    try {
      const data = await dataProvider.network.get("dash/master/counts");
      const ns = { ...masterCountData };

      for (let item of data) {
        switch (item.id) {
          case MASTER_ADVERTISER:
            ns.advertiser_count = item.value;
            break;
          case MASTER_BRAND:
            ns.brand_count = item.value;
            break;
          case MASTER_CHANNEL:
            ns.channel_count = item.value;
            break;
        }
      }

      setMasterCountData(ns);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch master count");
    }
  };

  const fetchPlayoutDates = async () => {
    try {
      const data = await dataProvider.network.get("dash/playout-dates");
      setPlayoutDates({
        min_date: new Date(data.min_date),
        max_date: new Date(data.max_date),
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to load playout dates");
    }
  };

  const fetchClientTypeCounts = async () => {
    try {
      const data = await dataProvider.network.get("dash/client-counts");
      let counts = [];

      counts.push(data.filter(x => x.id === CLIENT_ADVERTISER)[0].value);
      counts.push(data.filter(x => x.id === CLIENT_AGENCY)[0].value);
      counts.push(data.filter(x => x.id === CLIENT_BROADCASTER)[0].value);
      counts.push(data.filter(x => x.id === CLIENT_OTHERS)[0].value);

      setClientTypeCount(counts);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load client type counts");
    }
  };

  const fetchSpotTypeCounts = async () => {
    setSpotTrendLoader(true);
    try {
      const data = await dataProvider.network.get("dash/spot-type-counts");

      const state = [
        { name: "Commercial", data: data.map(x => x.commercial_count) },
        { name: "Promo", data: data.map(x => x.promo_count) },
      ];

      setSpotWiseData(state);
      setSpotCountDates(
        data.map(x => {
          return new Intl.DateTimeFormat("en-IN", {
            day: "numeric",
            month: "short",
          }).format(new Date(x.date));
        })
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to load spot type counts");
    } finally {
      setSpotTrendLoader(false);
    }
  };

  const fetchCriticalConsumptionGroups = async () => {
    try {
      const data = await dataProvider.network.get(
        "dash/critical-consumption-groups"
      );
      setCriticalConsumptionGroups(
        data.map(x => {
          return {
            ...x,
            consumption_percent: `${(
              (x.consumed_spots / x.total_spots) *
              100
            ).toFixed(2)}%`,
          };
        })
      );
    } catch (error) {
      console.error(error);
      toast.error(
        "Failed to load groups with 80% or more consumption of spots"
      );
    }
  };

  const checkPermission = perms => {
    if (perms.includes(token.type)) {
      return true;
    }

    return false;
  };

  const optionDonut = {
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      stroke: {
        colors: undefined,
      },
    },
    labels: ["Advertiser", "Agency", "Broadcaster", "Other"],
    legend: {
      position: "left",
      offsetY: 30,
      offsetX: 80,
    },
  };

  const optionBar = {
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        endingShape: "rounded",
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: spotCountDates,
    },
    tooltip: {
      y: {
        formatter: val => {
          return val.toLocaleString("en-IN");
        },
      },
    },
    fill: {
      opacity: 1,
    },
  };

  let tableList = listData.filter(x => checkPermission(x.permissions));

  return (
    <div id="dashboard" className="page">
      {largeModalContent && (
        <LargeModal onClose={() => setLargeModalContent(null)}>
          {largeModalContent}
        </LargeModal>
      )}
      <Header user={name} client_name={client_name} title="Dashboard" />
      <section className="content">
        {playoutDates && (
          <div className="flex data-date-row">
            <p className="right">
              Data date:{" "}
              <span>
                {new Intl.DateTimeFormat("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                }).format(playoutDates.min_date)}
              </span>{" "}
              to{" "}
              <span>
                {new Intl.DateTimeFormat("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                }).format(playoutDates.max_date)}
              </span>
            </p>
          </div>
        )}
        <div className="content-box flex flex-v-center pad-1">
          <div className="cards">
            <h4>Users</h4>
            <h1>{cardData.total_users}</h1>
          </div>
          <div className="cards">
            <h4>Subscriptions</h4>
            <h1>{cardData.total_subscriptions}</h1>
          </div>
          <div className="cards">
            <h4>Total Spots</h4>
            <h1>{cardData.total_spots.toLocaleString("en-IN")}</h1>
          </div>
          <div className="cards">
            <h4>Spots Consumed</h4>
            <h1>{cardData.spots_consumed.toLocaleString("en-IN")}</h1>
          </div>
          <div className="cards">
            <h4>Spots Available</h4>
            <h1>{cardData.spots_available.toLocaleString("en-IN")}</h1>
          </div>
          <div className="cards" title="Broadcaster Subscribed Channels">
            <h4>Broad. Sub. Channels</h4>
            <h1>
              {cardData.broadcaster_subscribed_channels.toLocaleString("en-IN")}
            </h1>
          </div>
        </div>
        <div className="content-box">
          <DataList list={tableList} />
        </div>
        <div className="flex mtop chart-container">
          <div className="box flex-full flex-col">
            <h3 className="text-center">Clients</h3>
            <div className="box">
              <ReactApexChart
                options={optionDonut}
                series={clientTypeCounts}
                type="donut"
                width="100%"
                height="200px"
              />
            </div>
          </div>
          <div className="box flex-full flex-col">
            <div className="flex flex-center">
              <h3 className="text-center">Spot Trend (Last 7 days)</h3>
              <div className="loader">
                {spotTrendLoader && (
                  <div className="lds-facebook">
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                )}
              </div>
            </div>
            <div className="box">
              <ReactApexChart
                options={optionBar}
                series={spotWiseData}
                type="bar"
                width="100%"
                height="200px"
              />
            </div>
          </div>
        </div>
        <div className="gap"></div>
      </section>
    </div>
  );
};

export default AdminDashboard;
